import React, { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { Input } from "rsuite";
import { updateReservationIds } from "../../../api";
import { UAL_SOURCES } from "../../../constants/ualActions";
import { validateIdField } from "../utils";
import TruncatedText from "../../../components/common/TruncatedText";
import EditIcon from "../../../assets/img/edit_pencil_line.svg";

const useInlineEdit = ({
  reservations,
  setReservations,
  isOffline,
  canEditReservations,
  getReservationReportRef,
}) => {
  const [editingField, setEditingField] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isUpdatingIds, setIsUpdatingIds] = useState(false);

  const prepareUpdatePayload = useCallback(
    (rowData, dataKey, currentValue) => {
      const reservation = reservations.find(
        (res) => res.htConfirmationCode === rowData.htConfirmationCode
      );

      const htRoomConfCode = rowData.htConfirmationCode;
      const htConfCode = htRoomConfCode.replace(/-\d+$/, "");

      const roomEntry = {
        htRoomConfCode,
        crsConfirmationId: reservation?.sourceRoomConfirmationNumber || null,
        pmsConfirmationId: reservation?.pmsId || null,
        crsCancellationCode: reservation?.cxlId || null,
      };

      if (dataKey === "pmsId") {
        roomEntry.pmsConfirmationId = currentValue || null;
        roomEntry.crsConfirmationId = currentValue || null;
      } else if (dataKey === "cxlId") {
        roomEntry.crsCancellationCode = currentValue || null;
      }

      return {
        adminComments: "",
        sendUpstream: false,
        htConfCode,
        roomsCrsCodes: [roomEntry],
      };
    },
    [reservations]
  );

  const handleSaveEdit = useCallback(
    async (rowData, dataKey, currentValue, errorKey) => {
      if (isUpdatingIds || !editingField) return;

      const error = validateIdField(
        currentValue,
        editingField.originalValue,
        dataKey
      );

      if (error) {
        toast.error(error);
        setReservations((prev) =>
          prev.map((res) =>
            res.htConfirmationCode === editingField.confirmationCode
              ? {
                  ...res,
                  [editingField.dataKey]: editingField.originalValue,
                }
              : res
          )
        );
        setEditingField(null);
        setFieldErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[errorKey];
          return newErrors;
        });
        return;
      }

      if (!currentValue || currentValue.trim() === "") {
        setEditingField(null);
        return;
      }

      const originalValue = editingField.originalValue || "";
      const trimmedCurrent = currentValue.trim();
      const trimmedOriginal = originalValue.trim();

      if (trimmedCurrent === trimmedOriginal) {
        setEditingField(null);
        return;
      }

      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });

      const payload = prepareUpdatePayload(rowData, dataKey, currentValue);

      try {
        setIsUpdatingIds(true);
        await updateReservationIds(payload, UAL_SOURCES.RESERVATION_REPORT);
        toast.success(
          `${dataKey === "pmsId" ? "PMS ID" : "CXL ID"} updated successfully`
        );
        setEditingField(null);
        if (getReservationReportRef.current) {
          await getReservationReportRef.current();
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            `Failed to update ${dataKey === "pmsId" ? "PMS ID" : "CXL ID"}. Please try again.`
        );
      } finally {
        setIsUpdatingIds(false);
      }
    },
    [
      isUpdatingIds,
      editingField,
      prepareUpdatePayload,
      setReservations,
      getReservationReportRef,
    ]
  );

  const handleExitEdit = useCallback(
    (rowData, dataKey, currentValue, errorKey) => {
      if (!editingField) return;

      const originalValue = editingField.originalValue || "";
      const hasChanged = currentValue.trim() !== originalValue.trim();

      if (hasChanged) {
        handleSaveEdit(rowData, dataKey, currentValue, errorKey);
      } else {
        setEditingField(null);
      }
    },
    [editingField, handleSaveEdit]
  );

  const enterEditMode = (rowData, dataKey, currentValue) => {
    setEditingField({
      confirmationCode: rowData.htConfirmationCode,
      dataKey: dataKey,
      originalValue: currentValue,
      wasEmpty: !currentValue || currentValue.trim() === "",
    });
  };

  const handleInputChange = (rowData, dataKey, errorKey, value) => {
    const updatedReservations = reservations.map((res) =>
      res.htConfirmationCode === rowData.htConfirmationCode
        ? { ...res, [dataKey]: value }
        : res
    );
    setReservations(updatedReservations);
    if (fieldErrors[errorKey]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const renderInlineEditCell = (rowData, dataKey) => {
    if (!isOffline || !canEditReservations) {
      return <TruncatedText text={rowData[dataKey]} placeholder="" />;
    }
    if (
      dataKey === "cxlId" &&
      String(rowData.statusValue).toLowerCase() === "active"
    ) {
      return <TruncatedText text={rowData[dataKey]} placeholder="" />;
    }

    const isEditing =
      editingField?.confirmationCode === rowData.htConfirmationCode &&
      editingField?.dataKey === dataKey;
    const errorKey = `${rowData.htConfirmationCode}-${dataKey}`;
    const hasError = !!fieldErrors[errorKey];
    const currentValue = rowData[dataKey] || "";

    if (!isEditing) {
      return (
        <button
          type="button"
          className="inline-edit-cell"
          onClick={() => enterEditMode(rowData, dataKey, currentValue)}
        >
          <TruncatedText
            className="cell-value"
            text={currentValue}
            placeholder=""
          />
          <img src={EditIcon} alt="edit" className="edit-pencil-icon" />
        </button>
      );
    }

    return (
      <div className="inline-edit-cell editing">
        <Input
          value={currentValue}
          onChange={(value) =>
            handleInputChange(rowData, dataKey, errorKey, value)
          }
          onBlur={() =>
            handleExitEdit(rowData, dataKey, currentValue, errorKey)
          }
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.target.blur();
            }
          }}
          autoFocus
          className={hasError ? "error-input" : ""}
        />
      </div>
    );
  };

  return {
    renderInlineEditCell,
  };
};

export default useInlineEdit;
